import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TextInput from "../forms/TextInput";
import ModalBase from "./ModalBase";
import { useModal } from "../../js/hooks/useModal";
import { useAuth } from "../../js/hooks/useAuth";
import { useStore } from "../../js/state/store";
import { validateLogin } from "../../js/utils/validators";
import { resolvePostAuthPath } from "../../js/auth/onboardingState";
import LifecycleLoginNote from "../account/LifecycleLoginNote";
import { isLifecycleLoginError } from "../../js/api/accountLifecycle";

export default function OfficeLoginDialog() {
  const officeModal = useModal("officeLogin");
  const { login } = useAuth();
  const { state, dispatch } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [lifeError, setLifeError] = useState(null);

  useEffect(() => {
    if (officeModal.isOpen) {
      setForm({ email: "", password: "" });
      setErrors({});
      setMessage("");
      setLifeError(null);
    }
  }, [officeModal.isOpen]);

  useEffect(() => {
    if (!officeModal.isOpen) return undefined;
    function onKey(event) {
      if (event.key === "Escape") officeModal.close();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [officeModal]);

  function update(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const nextErrors = validateLogin(form);
    setErrors(nextErrors);
    setMessage("");
    if (Object.keys(nextErrors).length) return;
    try {
      const intended = state.ui.pendingPath?.includes("broker") ? "broker" : "agent";
      let session;
      try {
        session = await login(form, intended);
      } catch (error) {
        if (isLifecycleLoginError(error)) throw error;
        session = await login(form, intended === "broker" ? "agent" : "broker");
      }
      officeModal.close();
      const nextPath = state.ui.pendingPath;
      dispatch({ type: "SET_PENDING_PATH", payload: "" });
      if (nextPath && nextPath.startsWith("/")) {
        navigate(nextPath);
        return;
      }
      if (session.role === "admin") {
        navigate("/admin");
        return;
      }
      navigate(resolvePostAuthPath(session, session.role));
    } catch (error) {
      setMessage(error.message || "Sign-in did not succeed.");
      setLifeError(error);
    }
  }

  return (
    <ModalBase isOpen={officeModal.isOpen} title="Office" onClose={officeModal.close} compact>
      <form className="office-login" onSubmit={handleSubmit} autoComplete="off">
        <TextInput
          label="Email"
          name="email"
          type="email"
          value={form.email}
          onChange={update}
          error={errors.email}
          autoComplete="username"
        />
        <TextInput
          label="Password"
          name="password"
          type="password"
          value={form.password}
          onChange={update}
          error={errors.password}
          autoComplete="current-password"
        />
        {message ? <p className="field-error">{message}</p> : null}
        <LifecycleLoginNote
          error={lifeError}
          form={form}
          onReactivated={() => handleSubmit({ preventDefault() {} })}
        />
        <button type="submit" className="office-login__submit">
          Sign in
        </button>
        <p className="office-login__forgot">
          <button
            type="button"
            className="text-btn"
            onClick={() => {
              officeModal.close();
              navigate("/forgot-password");
            }}
          >
            Forgot password?
          </button>
        </p>
      </form>
    </ModalBase>
  );
}
